import {
  Button,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AddIcon from '@mui/icons-material/Add';
import { useState } from "react";
import SearchBar from "../../../components/SearchBar";
import CommonBreadcrumb from "../../../components/CommonBreadcrumb";
import SwitchToggle from "../../../components/SwitchToggle";
import AddColor from "../../../Modals/addColor";
import React from "react";
import { useGetColorsQuery, useGetCategoriesQuery, useUpdateColorMutation, useDeleteColorMutation } from "../../../service/features/api";
import Pagination from "../../../components/Panigation";
import DeleteConfirmModal from "../../../Modals/DeleteConfirmModal";
import { usePermission } from "../../../hooks/usePermission";
import Can from "../../../components/Can";
import { toast } from "react-toastify";
import { getVehicleTypeLabel } from "../../../utils/vehicle";
import { VEHICLE_TYPE } from "../../../constants/enum";

const ManageColor = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canEdit = hasPermission("Vehicle Specification", "isEdit");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [category, setCategory] = React.useState('');
  const [vehicleType, setVehicleType] = useState<string>('');

  const handleCategoryChange = (event: SelectChangeEvent) => {
    setCategory(event.target.value as string);
    setPage(1);
  };

  const handleVehicleTypeChange = (event: SelectChangeEvent) => {
    setVehicleType(event.target.value as string);
    setCategory('');
    setPage(1);
  };



  const { data: categoriesData } = useGetCategoriesQuery({ 
    page: 1, 
    limit: 100,
    type: vehicleType || undefined,
  });
  const { data: colorsData, isLoading, isFetching } = useGetColorsQuery({
    page,
    limit,
    search: searchTerm,
    categoryId: category || undefined,
    type: vehicleType || undefined,
  });

  const [updateColor] = useUpdateColorMutation();
  const [deleteColor, { isLoading: isDeleting }] = useDeleteColorMutation();

  const colors = colorsData?.data?.data || [];
  const paginator = colorsData?.data?.paginator || {};

  const [openModal, setOpenModal] = useState(false);
  const [selectedColor, setSelectedColor] = useState<any>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedColor(null);
  };

  const handleEditClick = (row: any) => {
    setSelectedColor(row);
    setOpenModal(true);
  };

  const handleDeleteClick = (id: string) => {
    setItemToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (itemToDelete) {
      try {
        await deleteColor(itemToDelete).unwrap();
        toast.success("Color deleted successfully / تم حذف اللون بنجاح");
        setDeleteConfirmOpen(false);
        setItemToDelete(null);
      } catch (err: any) {
        toast.error(err?.data?.message || "Failed to delete / فشل الحذف");
      }
    }
  };

  const handleToggleStatus = async (row: any, currentStatus: boolean) => {
    const payload = {
      id: row._id,
      name: row.name,
      isActive: !currentStatus,
      categoryId: row.categories?.map((c: any) => c._id) || (row.categoryId ? (Array.isArray(row.categoryId) ? row.categoryId : [row.categoryId]) : []),
      type: row.type
    };

    try {
      await updateColor(payload).unwrap();
      toast.success("Status updated successfully / تم تحديث الحالة بنجاح");
    } catch (error: any) {
      console.error("Failed to update status", error);
      toast.error(error?.data?.message || "Failed to update status / فشل تحديث الحالة");
    }
  };

  const getVehicleTypeName = (value: number) => {
    return getVehicleTypeLabel(value) || "--";
  };

  return (
    <>
      <CommonBreadcrumb
        title="Color Management / إدارة الألوان"
        breadcrumbItems={[
          { label: "Dashboard / لوحة المعلومات", onClick: () => navigate("/dashboard") },
          { label: "Listed Vehicles / المركبات المدرجة", onClick: () => navigate("/manage-vehicles") },
          { label: "Color Management / إدارة الألوان" },
        ]}
      />

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              searchTerm={searchTerm}
              setDebouncedSearchTerm={(term) => {
                setSearchTerm(term);
                setPage(1);
              }}
              placeholder="Search Colors... / ابحث عن الألوان..."
            />
            <div className="form">
              <div className="control_group">
                <Select
                  fullWidth
                  value={vehicleType}
                  onChange={handleVehicleTypeChange}
                  displayEmpty
                >
                  <MenuItem value="">Select Vehicle Type / اختر نوع المركبة</MenuItem>
                  {Object.values(VEHICLE_TYPE)
                    .filter((v) => typeof v === "number")
                    .map((type) => (
                      <MenuItem key={type} value={type.toString()}>
                        {getVehicleTypeLabel(type as number)}
                      </MenuItem>
                    ))}
                </Select>
              </div>
              <div className="control_group">
                <Select
                  fullWidth
                  value={category}
                  onChange={handleCategoryChange}
                  displayEmpty
                >
                  <MenuItem value="">Select Category / اختر الفئة</MenuItem>
                  {categoriesData?.data?.data?.map((cat: any) => (
                    <MenuItem key={cat._id} value={cat._id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            </div>
          </div>
          <div className="right_s">
            <Can module="Vehicle Specification" action="isAdd" isDisabledMode={true}>
              <Button
                className="btn btn_primary"
                onClick={() => {
                  setSelectedColor(null);
                  setOpenModal(true);
                }}
              >
                <AddIcon /> Add Color / إضافة لون
              </Button>
            </Can>
          </div>
        </div>

        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Sr. No. / الرقم التسلسلي</TableCell>
                <TableCell>Color Detail / تفاصيل اللون</TableCell>
                <TableCell>Vehicle Type / نوع المركبة</TableCell>
                <TableCell>Categories / الفئات</TableCell>
                <TableCell>Created Date / تاريخ الإنشاء</TableCell>
                <TableCell>Disable/Enable / تعطيل/تفعيل</TableCell>
                <TableCell>Actions / الإجراءات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading || isFetching ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" style={{ padding: '2rem' }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : colors.length > 0 ? (
                colors.map((row: any, index: number) => (
                  <TableRow hover key={row._id}>
                    <TableCell>{(paginator.currentPage - 1) * paginator.perPage + index + 1}</TableCell>
                    <TableCell>
                      <div className="user_block click_action" onClick={() => handleEditClick(row)}>
                        <figure
                          className="no_radius"
                          style={{ backgroundColor: row.colorCode || row.name }}
                        ></figure>

                        <p>
                          <b>{row.name || "--"}</b>
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>{getVehicleTypeName(row.type)}</TableCell>
                    <TableCell>
                      {row.categories?.map((c: any) => c.name).join(", ") || '--'}
                    </TableCell>
                    <TableCell>{new Date(row.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {canEdit ? (
                        <SwitchToggle
                          checked={row.isActive}
                          onChange={() => handleToggleStatus(row, row.isActive)}
                        />
                      ) : (
                        <span className={`status_btn ${row.isActive ? "c_dark_info" : "c_dark_warning"}`}>
                          {row.isActive ? "Enabled / مفعل" : "Disabled / معطل"}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="table_actions v2">
                        <Can module="Vehicle Specification" action="isEdit" isDisabledMode={true}>
                          <Tooltip title="Edit / تعديل" arrow>
                            <IconButton
                              onClick={() => handleEditClick(row)}
                            >
                              <img src="/static/images/edit2_icon.svg" alt="Edit / تعديل" />
                            </IconButton>
                          </Tooltip>
                        </Can>
                        <Can module="Vehicle Specification" action="isDelete" isDisabledMode={true}>
                          <Tooltip title="Delete / حذف" arrow>
                            <IconButton onClick={() => handleDeleteClick(row._id)}>
                              <img src="/static/images/trash2_icon.svg" alt="Delete / حذف" />
                            </IconButton>
                          </Tooltip>
                        </Can>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No colors found / لا توجد ألوان
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {paginator && paginator.pageCount > 1 && (
          <Pagination
            module={colors}
            page={page}
            onPageChange={(p) => setPage(p)}
            totalPages={paginator.pageCount}
            totalCount={paginator.itemCount}
            setPage={setPage}
          />
        )}
      </div>

      <AddColor
        open={openModal}
        onClose={handleCloseModal}
        setOpen={setOpenModal}
        colorData={selectedColor}
      />

      <DeleteConfirmModal
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Color / حذف اللون"
        message="Are you sure you want to delete this color? / هل أنت متأكد أنك تريد حذف هذا اللون؟"
        isLoading={isDeleting}
      />
    </>
  );
};

export default ManageColor;
